import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { OtcSellOrder } from "../../types/cryptoSell.types";
import { cryptoSellOrderPath } from "../../utils/cryptoSwapLinks";
import { CryptoSellStatusPill } from "../sale/CryptoSellStatusPill";
import { SaleDetailsList } from "../sale/SaleDetailsList";
import { SaleAmounts } from "./SaleAmounts";

export interface CryptoSellOrderDrawerProps {
  sale: OtcSellOrder | null;
  isLoading: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** One sale, read-only, over the list. The deposit address lives only on the sale page. */
export function CryptoSellOrderDrawer({ sale, isLoading, open, onOpenChange }: CryptoSellOrderDrawerProps) {
  const { t, activeLanguage } = useTranslations("cryptoSwaps");

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={sale ? <SaleAmounts sale={sale} size="sm" /> : t("sell_drawer_title")}
      description={
        sale
          ? t("drawer_created_updated", {
              created: formatDateTime(sale.created_at, { locale: activeLanguage }),
              updated: formatDateTime(sale.updated_at, { locale: activeLanguage }),
            })
          : undefined
      }
      footer={
        sale ? (
          <ColocaButton
            variant="primary"
            iconTrailing={ArrowRight}
            className="w-full"
            render={<Link to={cryptoSellOrderPath(sale.id)} />}
          >
            {t("sell_drawer_open")}
          </ColocaButton>
        ) : undefined
      }
    >
      {isLoading || !sale ? (
        <>
          <ColocaSkeleton className="h-24 rounded-2xl" />
          <ColocaSkeleton className="mt-3 h-40 rounded-2xl" />
        </>
      ) : (
        <>
          <section className="pb-4">
            <CryptoSellStatusPill status={sale.status} />
          </section>
          <section className="flex flex-col gap-3 border-t border-border pt-4">
            <h3 className="text-xs font-semibold tracking-[0.08em] text-muted-foreground uppercase">
              {t("summary_title")}
            </h3>
            <SaleDetailsList sale={sale} />
          </section>
        </>
      )}
    </ColocaDrawer>
  );
}
